import React, { useEffect, useMemo, useRef, useState } from 'react';

import cx from 'classnames';
import { useTranslation } from 'react-i18next';

import { ICON_SET_FONTAWESOME } from 'constants-activate';
import { getDisplayName } from 'lane-shared/helpers';

import { Button, Flex, H4, Icon, Modal, P } from 'design-system-web';
import { ChannelAvatar } from '../ChannelAvatar';

import { ConfirmationModal } from './ConfirmationModal';
import type { HomeChannelPickerModalProps } from './types';
import {
  buildOrganisationGroups,
  filterGroups,
  findInitialExpandedOrgId,
  findInitialExpandedPropertyId,
} from './utils';

import styles from './HomeChannelPickerModal.scss';

function RadioButton({ selected }: { selected: boolean }) {
  return (
    <div className={cx(styles.radio, { [styles.radioSelected]: selected })}>
      {selected && <div className={styles.radioDot} />}
    </div>
  );
}

export function HomeChannelPickerModal({
  isOpen,
  onClose,
  channels,
  currentHomeChannelId,
  onConfirm,
  tenantsByPropertyId,
}: HomeChannelPickerModalProps) {
  const { t } = useTranslation();

  const groups = useMemo(
    () => buildOrganisationGroups(channels, tenantsByPropertyId),
    [channels, tenantsByPropertyId]
  );

  const [selectedChannelId, setSelectedChannelId] = useState<string | null>(
    currentHomeChannelId ?? null
  );
  const [isConfirming, setIsConfirming] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  const [expandedOrgIds, setExpandedOrgIds] = useState<Set<string>>(() => {
    const matchedId = findInitialExpandedOrgId(groups, currentHomeChannelId);

    return matchedId ? new Set([matchedId]) : new Set();
  });

  const [expandedPropertyIds, setExpandedPropertyIds] = useState<Set<string>>(
    () => {
      const initialId = findInitialExpandedPropertyId(
        groups,
        currentHomeChannelId
      );

      return initialId ? new Set([initialId]) : new Set();
    }
  );

  const allTenantChannels = useMemo(
    () =>
      tenantsByPropertyId
        ? Array.from(tenantsByPropertyId.values()).flat()
        : [],
    [tenantsByPropertyId]
  );

  const selectedChannel = useMemo(() => {
    if (!selectedChannelId) return undefined;

    return (
      channels.find(channel => channel._id === selectedChannelId) ??
      allTenantChannels.find(channel => channel._id === selectedChannelId)
    );
  }, [selectedChannelId, channels, allTenantChannels]);

  const visibleGroups = useMemo(
    () => filterGroups(groups, searchQuery),
    [groups, searchQuery]
  );

  const wasOpenRef = useRef(false);

  useEffect(() => {
    const justOpened = isOpen && !wasOpenRef.current;

    wasOpenRef.current = isOpen;

    if (!justOpened) return;

    setSelectedChannelId(currentHomeChannelId ?? null);
    setIsConfirming(false);
    setSaveError(null);
    setSearchQuery('');

    const orgId = findInitialExpandedOrgId(groups, currentHomeChannelId);

    setExpandedOrgIds(orgId ? new Set([orgId]) : new Set());

    const propId = findInitialExpandedPropertyId(groups, currentHomeChannelId);

    setExpandedPropertyIds(propId ? new Set([propId]) : new Set());
  }, [isOpen, currentHomeChannelId, groups]);

  function handleClose() {
    setIsConfirming(false);
    setSaveError(null);
    setSearchQuery('');
    onClose();
  }

  function handleCancelConfirmation() {
    setIsConfirming(false);
    setSaveError(null);
  }

  function toggleOrg(orgId: string) {
    setExpandedOrgIds(prev => {
      const next = new Set(prev);

      if (next.has(orgId)) {
        next.delete(orgId);
      } else {
        next.add(orgId);
      }

      return next;
    });
  }

  function toggleProperty(propertyId: string) {
    setExpandedPropertyIds(prev => {
      const next = new Set(prev);

      if (next.has(propertyId)) {
        next.delete(propertyId);
      } else {
        next.add(propertyId);
      }

      return next;
    });
  }

  async function handleConfirm() {
    if (!selectedChannel || isSaving) return;

    setIsSaving(true);
    setSaveError(null);

    try {
      await onConfirm(selectedChannel);
      handleClose();
    } catch {
      setSaveError(t('web.userProfile.homeChannel.saveError'));
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <>
      <Modal
        className={styles.modal}
        isOpen={isOpen && !isConfirming}
        onClose={handleClose}
        title={t('web.userProfile.homeChannel.selectHomeChannel')}
        testId="homeChannelPickerModal"
        includeHeader={false}
        isCloseButtonHidden
        disableContentPadding
      >
        <div className={styles.header}>
          <Flex justify="space-between" align="flex-start">
            <div>
              <H4 className={styles.title}>
                {t('web.userProfile.homeChannel.selectHomeChannel')}
              </H4>
              <P className={styles.subtitle}>
                {t('web.userProfile.homeChannel.subtitle')}
              </P>
            </div>
            <button
              className={styles.closeButton}
              onClick={handleClose}
              type="button"
              data-test="closeButton"
              aria-label={t('Close')}
            >
              <Icon name="times" set={ICON_SET_FONTAWESOME} type="far" />
            </button>
          </Flex>

          <div className={styles.searchBar}>
            <Icon
              className={styles.searchIcon}
              name="search"
              set={ICON_SET_FONTAWESOME}
              type="far"
            />
            <input
              className={styles.searchInput}
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder={t('web.userProfile.homeChannel.searchChannels')}
              type="text"
            />
          </div>
        </div>

        <div className={styles.list}>
          {visibleGroups.map((group, index) => {
            const orgId = group.organisation?._id;
            const isOrgExpanded =
              Boolean(searchQuery.trim()) ||
              (orgId ? expandedOrgIds.has(orgId) : true);

            return (
              <div
                key={orgId || `ungrouped-${index}`}
                className={styles.orgGroupContainer}
              >
                {group.organisation && (
                  <div
                    className={styles.orgRow}
                    onClick={() => orgId && toggleOrg(orgId)}
                    onKeyDown={e => {
                      if ((e.key === 'Enter' || e.key === ' ') && orgId) {
                        e.preventDefault();
                        toggleOrg(orgId);
                      }
                    }}
                    role="button"
                    tabIndex={0}
                    data-test={`orgRow-${orgId}`}
                  >
                    <button
                      className={styles.chevron}
                      tabIndex={-1}
                      type="button"
                    >
                      <Icon
                        name={isOrgExpanded ? 'chevron-down' : 'chevron-right'}
                        set={ICON_SET_FONTAWESOME}
                        type="far"
                      />
                    </button>
                    <ChannelAvatar
                      name={getDisplayName(group.organisation)}
                      logo={group.organisation?.profile?.logo}
                    />
                    <span className={styles.rowTitle}>
                      {getDisplayName(group.organisation)}
                    </span>
                    <span className={styles.badge}>
                      {t('web.userProfile.homeChannel.organisation')}
                    </span>
                  </div>
                )}

                {isOrgExpanded &&
                  group.properties.map(({ property, tenants }) => {
                    const isPropertyExpanded =
                      Boolean(searchQuery.trim()) ||
                      expandedPropertyIds.has(property._id);
                    const isPropertySelected =
                      selectedChannelId === property._id;
                    const hasTenants = tenants.length > 0;

                    return (
                      <div
                        key={property._id}
                        className={styles.propertyGroupContainer}
                      >
                        <div
                          className={cx(styles.propertyRow, {
                            [styles.propertyRowHighlighted]: isPropertySelected,
                            [styles.propertyRowIndented]: Boolean(
                              group.organisation
                            ),
                          })}
                          onClick={() => setSelectedChannelId(property._id)}
                          onKeyDown={e => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              setSelectedChannelId(property._id);
                            }
                          }}
                          role="button"
                          tabIndex={0}
                          data-test={`homeChannelOption-${property._id}`}
                        >
                          {hasTenants ? (
                            <button
                              className={styles.chevron}
                              tabIndex={-1}
                              type="button"
                              data-test={`expand-${property._id}`}
                              onClick={e => {
                                e.stopPropagation();
                                toggleProperty(property._id);
                              }}
                            >
                              <Icon
                                name={
                                  isPropertyExpanded
                                    ? 'chevron-down'
                                    : 'chevron-right'
                                }
                                set={ICON_SET_FONTAWESOME}
                                type="far"
                              />
                            </button>
                          ) : (
                            <div className={styles.chevronPlaceholder} />
                          )}
                          <RadioButton selected={isPropertySelected} />
                          <ChannelAvatar
                            name={getDisplayName(property)}
                            logo={property.profile?.logo}
                          />
                          <div className={styles.rowTitleWrapper}>
                            <span className={styles.rowTitle}>
                              {getDisplayName(property)}
                            </span>
                            <span
                              className={cx(styles.badge, styles.badgeProperty)}
                            >
                              {t('web.userProfile.homeChannel.property')}
                            </span>
                          </div>
                        </div>

                        {isPropertyExpanded && hasTenants && (
                          <div className={styles.tenantTreeList}>
                            {tenants.map(tenant => {
                              const isTenantSelected =
                                selectedChannelId === tenant._id;

                              return (
                                <div
                                  key={tenant._id}
                                  className={cx(styles.tenantRow, {
                                    [styles.tenantRowSelected]:
                                      isTenantSelected,
                                  })}
                                  onClick={() =>
                                    setSelectedChannelId(tenant._id)
                                  }
                                  onKeyDown={e => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                      e.preventDefault();
                                      setSelectedChannelId(tenant._id);
                                    }
                                  }}
                                  role="button"
                                  tabIndex={0}
                                  data-test={`homeChannelOption-${tenant._id}`}
                                >
                                  <div className={styles.treeBranchLine} />
                                  <RadioButton selected={isTenantSelected} />
                                  <ChannelAvatar
                                    name={getDisplayName(tenant)}
                                    logo={tenant.profile?.logo}
                                  />
                                  <div className={styles.rowTitleWrapper}>
                                    <span className={styles.rowTitle}>
                                      {getDisplayName(tenant)}
                                    </span>
                                    <span
                                      className={cx(
                                        styles.badge,
                                        styles.badgeTenant
                                      )}
                                    >
                                      {t('web.userProfile.homeChannel.tenant')}
                                    </span>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
              </div>
            );
          })}
        </div>

        <Flex justify="space-between" align="center" className={styles.footer}>
          <P
            className={cx(styles.selectedLabel, {
              [styles.selectedLabelEmpty]: !selectedChannel,
            })}
          >
            {selectedChannel
              ? t('web.userProfile.homeChannel.selectedLabel', {
                  name: getDisplayName(selectedChannel),
                })
              : t('web.userProfile.homeChannel.noChannelSelected')}
          </P>
          <Flex gap={2}>
            <Button
              className={styles.cancelBtn}
              variant="secondary"
              onClick={handleClose}
            >
              {t('web.userProfile.homeChannel.cancel')}
            </Button>
            <Button
              className={styles.submitBtn}
              variant="primary"
              disabled={!selectedChannelId}
              onClick={() => setIsConfirming(true)}
              testId="confirmHomeChannelSelection"
            >
              {t('web.userProfile.homeChannel.setAsHomeChannel')}
            </Button>
          </Flex>
        </Flex>
      </Modal>

      <ConfirmationModal
        isOpen={isOpen && isConfirming && Boolean(selectedChannel)}
        selectedChannel={selectedChannel}
        isSaving={isSaving}
        saveError={saveError}
        onCancel={handleCancelConfirmation}
        onConfirm={handleConfirm}
      />
    </>
  );
}
